import { api } from '../axios';
import Mongo from './Mongo';
import Authentication from './Authentication';
import {
  QuizObj,
  QuizWhere,
  EveResponse,
  EveWhere,
  NoAuthError,
  QuizHistoryWhere,
  QuizHistory,
  EveUpdate,
  Messages,
  Objective,
} from '../../types';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Logger } from '../Logger';

const QUESTIONS_ENDPOINT = '/questions';
const QUIZ_ENDPOINT = '/quiz';
const QUIZ_HISTORY = '/quiz_history';

class Quiz extends Mongo {
  private logger = new Logger('Quiz Services');

  public async fetch({
    where = {},
    page = 1,
    max = 50,
    sortBy = [['_updated', 'desc']],
  }: EveWhere<QuizWhere>): Promise<QuizObj[]> {
    const user = Authentication.loggedUser();
    if (!user) throw NoAuthError;
    const flattenQuery = this.flattenMongoQuery({
      query: {
        ...where,
        area: user.area.name,
      },
    }) as unknown;
    const axiosConfig: AxiosRequestConfig = {
      params: {
        page,
        max_results: max,
        where: flattenQuery,
        // sort: this.parseSortArr(sortBy),
      },
      headers: {
        ContestId: user.contest._id,
      },
    };
    return api
      .get(QUIZ_ENDPOINT, axiosConfig)
      .then((response: AxiosResponse<EveResponse<QuizObj[]>>) => {
        return response.data._items;
      });
  }

  public async fetchQuestions(
    qIds: string[]
  ): Promise<EveResponse<Objective[]> | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const where = {
        qid: {
          $in: qIds,
        },
      };
      const flattenQuery = this.flattenMongoQuery({ query: where }) as unknown;

      const axiosConfig: AxiosRequestConfig = {
        params: {
          max_results: qIds.length,
          where: flattenQuery,
        },
      };

      const response: AxiosResponse<EveResponse<Objective[]>> = await api.get(
        QUESTIONS_ENDPOINT,
        axiosConfig
      );
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  public async fetchQuizById(quizId: string): Promise<QuizObj | undefined> {
    const loginData = Authentication.loggedUser();
    if (!loginData) throw NoAuthError;
    const response = await api.get<QuizObj>(`${QUIZ_ENDPOINT}/${quizId}`);
    return response.data;
  }

  public async fetchQuizHistoryById(id: string): Promise<QuizHistory> {
    const loginData = Authentication.loggedUser();
    if (!loginData) throw NoAuthError;
    return api.get<QuizHistory>(`${QUIZ_HISTORY}/${id}`).then((response) => {
      return response.data;
    });
  }

  public async fetchActive({
    where = {},
    page = 1,
    max = 10,
    sortBy = [['_created', 'desc']],
  }: EveWhere<QuizWhere>): Promise<QuizObj[] | undefined> {
    const loginData = Authentication.loggedUser();
    if (!loginData) return;
    const query = {
      ...where,
      showing: true,
      date_end: {
        $gte: new Date().toUTCString(),
      },
      date_start: {
        $lte: new Date().toUTCString(),
      },
      contests: {
        $in: [loginData.plan.current_contest],
      },
      area: loginData.area.name,
    };
    const flattenQuery = this.flattenMongoQuery({ query }) as unknown;
    const axiosConfig: AxiosRequestConfig = {
      params: {
        page,
        max_results: max,
        where: flattenQuery,
        // sort: this.parseSortArr(sortBy),
      },
    };
    return api
      .get(QUIZ_ENDPOINT, axiosConfig)
      .then((response: AxiosResponse<EveResponse<QuizObj[]>>) => {
        return response.data._items;
      });
  }

  public async fetchFinishedQuiz({
    where = {},
    page = 1,
    max = 10,
    sortBy = [['_created', 'desc']],
  }: EveWhere<QuizHistoryWhere>): Promise<QuizHistory[] | undefined> {
    const loginData = Authentication.loggedUser();
    if (!loginData) return;
    const flattenQuery = this.flattenMongoQuery({
      query: {
        ...where,
        area: loginData.area.name,
        user_email: loginData.user.email,
        contests: {
          $in: [loginData.plan.current_contest],
        },
        status: 'finished',
      },
    }) as unknown;
    const axiosConfig: AxiosRequestConfig = {
      params: {
        page,
        max_results: max,
        where: flattenQuery,
        // sort: this.parseSortArr(sortBy),
      },
      headers: {
        'Cache-Control': 'no-cache',
      },
    };
    return api
      .get(QUIZ_HISTORY, axiosConfig)
      .then((response: AxiosResponse<EveResponse<QuizHistory[]>>) => {
        return response.data._items;
      });
  }

  public async fetchStartedQuiz({
    where = {},
    page = 1,
    max = 10,
    sortBy = [['_created', 'desc']],
  }: EveWhere<QuizHistoryWhere>): Promise<QuizHistory[] | undefined> {
    const loginData = Authentication.loggedUser();
    if (!loginData) return;
    const flattenQuery = this.flattenMongoQuery({
      query: {
        ...where,
        area: loginData.area.name,
        user_email: loginData.user.email,
        contests: {
          $in: [loginData.plan.current_contest],
        },
        status: 'started',
      },
    }) as unknown;
    const axiosConfig: AxiosRequestConfig = {
      params: {
        page,
        max_results: max,
        where: flattenQuery,
        // sort: this.parseSortArr(sortBy),
      },
      headers: {
        'Cache-Control': 'no-cache',
      },
    };
    return api
      .get(QUIZ_HISTORY, axiosConfig)
      .then((response: AxiosResponse<EveResponse<QuizHistory[]>>) => {
        return response.data._items;
      });
  }

  public async saveQuizHistory(
    quizHistory: QuizHistoryWhere
  ): Promise<EveUpdate | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      if (quizHistory._id) {
        const contests = quizHistory.contests?.find(
          (el) => el === loginData.plan.current_contest
        );
        if (!contests)
          quizHistory.contests?.push(loginData.plan.current_contest);
        const response = await api.patch<EveUpdate>(
          `${QUIZ_HISTORY}/${quizHistory._id}`,
          {
            answer_statistics: quizHistory.answer_statistics,
            area: loginData.area.name,
            contests: quizHistory.contests,
            duration: quizHistory.duration,
            quiz_id: quizHistory.quiz_id,
            user_email: loginData?.user?.email,
            date_made: new Date().toUTCString(),
            status: quizHistory.status,
          },
          {
            headers: {
              'X-Org-Id': loginData?.permissions[0]?.group?.slug,
              UserEmail: loginData?.user?.email,
              'If-Match': quizHistory._etag,
            },
          }
        );
        return response.data;
      } else {
        const response = await api.post<EveUpdate>(QUIZ_HISTORY, {
          ...quizHistory,
          contests: [loginData.plan.current_contest],
          area: loginData.area.name,
          user_email: loginData?.user?.email,
        });
        return response.data;
      }
    } catch (error) {
      this.logger.log('error', Messages.QUIZ_SAVE_HISTORY_ERROR, error);
    }
  }
  public async updateDuration(
    quiz: QuizHistoryWhere
  ): Promise<EveUpdate | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData || !quiz._id) return;
      return await api.patch(
        `${QUIZ_HISTORY}/${quiz._id}`,
        {
          duration: quiz.duration,
        },
        {
          headers: {
            'X-Org-Id': loginData?.permissions[0]?.group?.slug,
            UserEmail: loginData?.user?.email,
            'If-Match': quiz._etag,
          },
        }
      );
    } catch (error) {
      this.logger.log('error', Messages.QUIZ_UPDATE_DURATION, error);
    }
  }
}

export default new Quiz();
